import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import {
  Shield, Loader2, Search, Filter, ChevronLeft, ChevronRight,
  CheckCircle, XCircle, RotateCcw, RefreshCw,
} from "lucide-react";

const PAGE_SIZE = 50;

const EVENT_META = {
  dev_login_used: {
    label: "DEV LOGIN — SUCCESS",
    color: "#FBBF24",  // amber: a privileged action succeeded
    Icon: CheckCircle,
  },
  dev_login_blocked: {
    label: "DEV LOGIN — BLOCKED",
    color: "#4ADE80",  // green: defence triggered
    Icon: XCircle,
  },
  sessions_wiped: {
    label: "SESSIONS WIPED",
    color: "#36A9E1",  // blue: admin-triggered rotation
    Icon: RotateCcw,
  },
};

const fmtTimestamp = (ts) => {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
};

const SecurityAuditLogsPage = () => {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [eventType, setEventType] = useState("");
  const [userIdFilter, setUserIdFilter] = useState("");
  const [offset, setOffset] = useState(0);
  const [eventTypes, setEventTypes] = useState([]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset };
      if (eventType) params.event_type = eventType;
      if (userIdFilter.trim()) params.user_id = userIdFilter.trim();
      const res = await axios.get(`${API}/admin/security-audit-logs`, {
        withCredentials: true,
        params,
      });
      setEntries(res.data?.entries || []);
      setTotal(res.data?.total || 0);
      if (res.data?.event_types) setEventTypes(res.data.event_types);
    } catch (e) {
      console.error("security-audit-logs fetch failed:", e);
      setEntries([]);
      setTotal(0);
    }
    setLoading(false);
  }, [eventType, userIdFilter, offset]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const renderRow = (entry, idx) => {
    const meta = EVENT_META[entry.event_type] || {
      label: (entry.event_type || "UNKNOWN").toUpperCase(),
      color: "#8B95A5",
      Icon: Shield,
    };
    const RowIcon = meta.Icon;
    const subjectId = entry.user_id || entry.actor_user_id || "—";
    const subjectEmail = entry.email || entry.actor_email || "";
    const reason = entry.reason ? ` · reason: ${entry.reason}` : "";
    const host = entry.host ? ` · host: ${entry.host}` : "";
    const role = entry.role ? ` · role: ${entry.role}` : "";
    const sessions = entry.sessions_deleted != null
      ? ` · sessions_deleted: ${entry.sessions_deleted}` : "";

    return (
      <div
        key={`${entry.timestamp}-${idx}`}
        className="flex items-start gap-4 px-4 py-3 bg-[#111318] rounded hover:bg-[#181B23] transition-colors"
        data-testid={`security-audit-row-${idx}`}
      >
        <RowIcon size={16} className="shrink-0 mt-0.5" style={{ color: meta.color }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white text-xs font-semibold tracking-wide">
              {meta.label}
            </span>
            {entry.ip_address && (
              <span className="text-[#36A9E1] text-[10px] font-mono">
                {entry.ip_address}
              </span>
            )}
          </div>
          <p className="text-[#8B95A5] text-[11px] mt-0.5 break-all">
            <span className="text-[#C5CCD6]">{subjectId}</span>
            {subjectEmail && <span> ({subjectEmail})</span>}
            {role}{host}{reason}{sessions}
          </p>
          {entry.user_agent && (
            <p className="text-[#64748B] text-[10px] mt-0.5 truncate">
              UA: {entry.user_agent}
            </p>
          )}
        </div>
        <div className="text-right shrink-0">
          <p className="text-[#8B95A5] text-[10px]">
            {fmtTimestamp(entry.timestamp)}
          </p>
          {entry.session_token_prefix && (
            <p className="text-[#64748B] text-[9px] font-mono mt-0.5">
              {entry.session_token_prefix}…
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <QMSLayout title="Security Audit Logs">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2
            className="font-['Syne'] text-2xl font-bold text-white"
            data-testid="security-audit-logs-title"
          >
            Security Audit Logs
          </h2>
          <p className="text-[#8B95A5] text-sm mt-1">
            Forensic trail of dev-login attempts, blocked invocations, and
            admin-triggered session rotations. {total.toLocaleString()} total
            event{total === 1 ? "" : "s"}.
          </p>
        </div>
        <button
          onClick={() => { setOffset(0); fetchLogs(); }}
          className="flex items-center gap-2 bg-[#1F222D] hover:bg-[#262A37] rounded px-3 py-2 text-white text-xs transition-colors"
          data-testid="security-audit-logs-refresh"
        >
          <RefreshCw size={12} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="qms-card mb-4">
        <div className="qms-card-body flex items-end gap-3 flex-wrap">
          <div className="flex flex-col gap-1">
            <label className="text-[#8B95A5] text-[10px] uppercase tracking-wide flex items-center gap-1">
              <Filter size={10} /> Event Type
            </label>
            <select
              value={eventType}
              onChange={(e) => { setOffset(0); setEventType(e.target.value); }}
              className="bg-[#1F222D] text-white text-xs rounded px-3 py-2 outline-none border border-transparent focus:border-[#36A9E1]"
              data-testid="security-audit-logs-event-type-filter"
            >
              <option value="">All event types</option>
              {eventTypes.map((t) => (
                <option key={t} value={t}>
                  {EVENT_META[t]?.label || t}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
            <label className="text-[#8B95A5] text-[10px] uppercase tracking-wide flex items-center gap-1">
              <Search size={10} /> User ID (exact)
            </label>
            <input
              type="text"
              value={userIdFilter}
              onChange={(e) => setUserIdFilter(e.target.value)}
              onBlur={() => { setOffset(0); fetchLogs(); }}
              onKeyDown={(e) => {
                if (e.key === "Enter") { setOffset(0); fetchLogs(); }
              }}
              placeholder="e.g. user_dev_admin or muchina@charstan.com user_id"
              className="bg-[#1F222D] text-white text-xs rounded px-3 py-2 outline-none border border-transparent focus:border-[#36A9E1]"
              data-testid="security-audit-logs-user-id-filter"
            />
          </div>
          {(eventType || userIdFilter) && (
            <button
              onClick={() => {
                setEventType("");
                setUserIdFilter("");
                setOffset(0);
              }}
              className="text-[#8B95A5] hover:text-white text-xs underline"
              data-testid="security-audit-logs-clear-filters"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="qms-card">
        <div className="qms-card-body">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="animate-spin text-[#36A9E1]" size={24} />
            </div>
          ) : entries.length === 0 ? (
            <div
              className="text-center text-[#8B95A5] py-12"
              data-testid="security-audit-logs-empty"
            >
              <Shield size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">
                No security events match your filters.
              </p>
              <p className="text-[11px] mt-1 opacity-70">
                Forward-going capture began 2026-05-01. Pre-hook
                invocations are not recoverable.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {entries.map(renderRow)}
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {!loading && total > PAGE_SIZE && (
        <div
          className="flex items-center justify-between mt-4 text-xs text-[#8B95A5]"
          data-testid="security-audit-logs-pagination"
        >
          <span>
            Page {currentPage} of {totalPages} · showing{" "}
            {entries.length === 0 ? 0 : offset + 1}–
            {offset + entries.length} of {total.toLocaleString()}
          </span>
          <div className="flex items-center gap-2">
            <button
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              className="flex items-center gap-1 bg-[#1F222D] hover:bg-[#262A37] disabled:opacity-30 disabled:cursor-not-allowed rounded px-3 py-1.5 text-white transition-colors"
              data-testid="security-audit-logs-prev-page"
            >
              <ChevronLeft size={12} /> Prev
            </button>
            <button
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
              className="flex items-center gap-1 bg-[#1F222D] hover:bg-[#262A37] disabled:opacity-30 disabled:cursor-not-allowed rounded px-3 py-1.5 text-white transition-colors"
              data-testid="security-audit-logs-next-page"
            >
              Next <ChevronRight size={12} />
            </button>
          </div>
        </div>
      )}
    </QMSLayout>
  );
};

export default SecurityAuditLogsPage;
